import React, { useCallback, useContext, useState } from 'react';
import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';
import { Activity } from 'botframework-directlinejs';
import { DirectLineContext } from '../components/direct-line-provider/direct-line-context';
import { IAdsCopilotProps } from '../types';
import { Loading } from '../Loading';
import { StopGenerating } from '../StopGenerating';
import { getOAuthCardResourceUri } from './useSSO';
import { BotActivity, StreamingStatusType } from '../botTypes';
import { isTypingMessage, EVENT_NAME_ENGAGE_TROUBLESHOOTER, isTroubleshootingEvent } from '../utils';
import { StopTroubleshooting } from '../StopTroubleshooting';

const IgnoreToggleTypes = ['messageReaction', 'endOfConversation'];

interface IUseLoadingProps {
  onReceiveActivity?: IAdsCopilotProps['onReceiveActivity'];
  renderBottomComponent?: IAdsCopilotProps['renderBottomComponent'];
  isCustomStream?: boolean;
  isStopGeneratingEnabled?: IAdsCopilotProps['isStopGeneratingEnabled'];
  isStopTroubleshootingEnabled?: IAdsCopilotProps['isStopTroubleshootingEnabled'];
}

export const shouldToggleLoading = (activity: Activity) => {
  const isIgnoredType = IgnoreToggleTypes.indexOf(activity.type) > -1;
  return !isIgnoredType && !isTroubleshootingEvent(activity);
};

export const shouldToggleTroubleShooting = (activity: Activity) => isTroubleshootingEvent(activity);

export function useLoading({
  onReceiveActivity,
  renderBottomComponent,
  isCustomStream,
  isStopGeneratingEnabled,
  isStopTroubleshootingEnabled,
}: IUseLoadingProps) {
  const [loading, setLoading] = useState(false);
  const [troubleshooting, setTroubleshooting] = useState(false);
  const [postedMessageId, setPostedMessageId] = useState<string | undefined>(undefined);
  const i18n = useContext(I18nContext) as I18nModel;
  const { directLine, userid, username } = useContext(DirectLineContext)!;

  const onReceiveActivityWithLoading = useCallback<NonNullable<IAdsCopilotProps['onReceiveActivity']>>((activity, currentActivities, dl) => {
    const messageActivity = activity as BotActivity;
    if (shouldToggleLoading(activity) && !isCustomStream) {
      if (activity.from.name === username) {
        setLoading(true);
        setPostedMessageId(activity.id);
      } else if (activity.type === 'message' && !isTypingMessage(activity) && getOAuthCardResourceUri(activity) == null) {
        if (messageActivity.value?.StreamingStatus === StreamingStatusType.InProgress) {
          setLoading(true);
        } else {
          setLoading(false);
        }
      }
    } else if (shouldToggleTroubleShooting(activity)) {
      if (messageActivity.value?.troubleshootingValue?.troubleshootingStatus === 'InProgress') {
        setTroubleshooting(true);
      } else {
        setTroubleshooting(false);
      }
    }

    if (onReceiveActivity) {
      return onReceiveActivity(activity, currentActivities, dl);
    }
    return currentActivities;
  }, [isCustomStream, onReceiveActivity, username]);

  const onClickStopGenerating = useCallback(() => {
    const stopRespondingMessage = {
      type: 'event',
      replyToId: postedMessageId,
      name: 'stopResponding',
      from: {
        id: userid,
        name: username,
      },
    };

    directLine?.postActivity(stopRespondingMessage as any).subscribe();
  }, [directLine, postedMessageId, userid, username]);

  const onClickStopTroubleshooting = useCallback(() => {
    const stopTroubleshootingMessage = {
      type: 'event',
      name: EVENT_NAME_ENGAGE_TROUBLESHOOTER,
      from: {
        id: userid,
        name: username,
      },
      value: {
        isForceStopTroubleshooting: true,
        Features: ['Troubleshooting'],
      },
    };

    directLine?.postActivity(stopTroubleshootingMessage as any).subscribe();
  }, [directLine, userid, username]);

  const renderBottomComponentWithLoading = useCallback<NonNullable<IAdsCopilotProps['renderBottomComponent']>>((currentActivities) => {
    if (loading) {
      if (isStopGeneratingEnabled) {
        return (
          <StopGenerating onClickStopGenerating={onClickStopGenerating} />
        );
      }

      return (
        <Loading loadingText={i18n.getString(_TL_('Generating answers...'))} />
      );
    } if (troubleshooting && isStopTroubleshootingEnabled) {
      return (
        <StopTroubleshooting onClickStopTroubleshooting={onClickStopTroubleshooting} />
      );
    }

    if (renderBottomComponent) {
      return renderBottomComponent(currentActivities);
    }

    return null;
  }, [i18n, loading, onClickStopGenerating, renderBottomComponent, isStopGeneratingEnabled, troubleshooting, isStopTroubleshootingEnabled]);

  return {
    onReceiveActivity: onReceiveActivityWithLoading,
    renderBottomComponent: renderBottomComponentWithLoading,
  };
}
